import PocketBase, { type RecordModel } from 'pocketbase'
import type {
  ActivityEntry,
  Announcement,
  AnnouncementSeverity,
  Billing,
  Candidate,
  Recruiter,
  RecruiterPrefs,
  SeatRole,
  TeamMember,
  Vouch,
} from './types'
import { POCKETBASE_URL } from './config'

// ─────────────────────────────────────────────────────────────────────────
// The single shared PocketBase client + record→domain-type mappers.
//
// The default LocalAuthStore persists the auth token in localStorage, so a
// recruiter stays signed in across reloads. (The old demo store kept everything
// in memory and reset on every refresh — now that there's a real backend, a
// persisted session is the expected behaviour.)
// ─────────────────────────────────────────────────────────────────────────

export const pb = new PocketBase(POCKETBASE_URL)

// We fan out several reads in parallel on load; without this the SDK would
// auto-cancel all-but-the-last in-flight request and abort legitimate fetches.
pb.autoCancellation(false)

// ── Session expiry: one place to end a dead session ──────────────────────────
// authStore.onChange (store.tsx) fires on login/logout/refresh but NOT when a
// token passively expires — so a stale session would otherwise linger (authed
// stuck true) and every page would start 401-ing in its own way. onAuthExpired()
// clears the token, which fires onChange → authed flips false → RequireAuth
// redirects to /login. It's called from three places: any 401 from a PB SDK call
// (the hook below), any 401 from the /api/* fetch wrappers, and the store's
// idle-expiry poll. Self-guards, so it's a no-op when already signed out.
export function onAuthExpired(): void {
  if (pb.authStore.token) pb.authStore.clear()
}

// Every PocketBase SDK response passes through here — including the candidate-pool,
// billing, team and activity reads that populate the console. A 401 means the
// session is no longer valid, so end it cleanly instead of failing the page.
pb.afterSend = (response, data) => {
  if (response.status === 401) onAuthExpired()
  return data
}

/** The auth collection recruiters authenticate against. */
export const RECRUITERS = 'recruiters'

/** Preference defaults applied when a recruiter has no `prefs` stored yet. */
export const DEFAULT_PREFS: RecruiterPrefs = {
  emailDigest: true,
  surfaceVerified: true,
  liveAlerts: false,
}

// Records come back with json fields already parsed, so each mapper just picks
// the typed shape out of the loosely-typed record (and drops PB system fields).

export function toRecruiter(r: RecordModel | null | undefined): Recruiter | null {
  if (!r) return null
  return {
    name: r.name || r.email,
    email: r.email,
    company: r.company || '',
    domain: r.domain || '',
    role: r.role || '',
    seatRole: (r.seatRole as SeatRole) || 'recruiter',
    initials: r.initials || initialsOf(r.name || r.email),
    avatarHue: typeof r.avatarHue === 'number' ? r.avatarHue : 268,
    prefs: { ...DEFAULT_PREFS, ...(r.prefs ?? {}) },
    accountId: r.accountId || undefined,
    isStaff: !!r.isStaff,
  }
}

export function toCandidate(r: RecordModel): Candidate {
  return {
    id: r.id,
    name: r.name,
    headline: r.headline,
    location: r.location,
    avatarHue: r.avatarHue,
    initials: r.initials,
    tier: r.tier,
    state: r.state,
    signals: r.signals ?? [],
    accountId: r.accountId || undefined,
    liveVouch: r.liveVouch ?? { status: 'none' },
    resume: r.resume ?? { match: 'unverified', hash: '—', fileName: '—' },
    yearsExperience: r.yearsExperience,
    topSkills: r.topSkills ?? [],
    unlocked: r.unlocked,
    verifyRequest: r.verifyRequest,
    addedAt: r.addedAt,
    lastActive: r.lastActive,
    establishedAt: r.establishedAt || undefined,
    deviceLocation: r.deviceLocation?.country ? r.deviceLocation : undefined,
    origin: r.origin || undefined,
    custody: r.custody || undefined,
    invitedByRecruiterId: r.invitedByRecruiterId || undefined,
  }
}

export function toVouch(r: RecordModel): Vouch {
  return {
    id: r.id,
    voucherPseudoId: r.voucherPseudoId,
    voucherName: r.voucherName || undefined,
    voucherInitials: r.voucherInitials || undefined,
    voucherHue: typeof r.voucherHue === 'number' ? r.voucherHue : undefined,
    subjectId: r.subjectId,
    voucherDeviceKeyB64: r.voucherDeviceKeyB64 || '',
    claimedEmployer: r.claimedEmployer || '',
    overlapStart: r.overlapStart || undefined,
    overlapEnd: r.overlapEnd || undefined,
    relationship: r.relationship || '',
    voucherSignatureB64: r.voucherSignatureB64 || '',
    tenureProof: r.tenureProof ?? { mode: 'claimed' },
    createdAt: r.createdAt || r.created,
  }
}

export function toActivity(r: RecordModel): ActivityEntry {
  return {
    id: r.id,
    kind: r.kind,
    at: r.at,
    actor: r.actor,
    candidateId: r.candidateId || undefined,
    candidateName: r.candidateName || undefined,
    detail: r.detail || undefined,
  }
}

export function toTeam(r: RecordModel): TeamMember {
  return {
    id: r.id,
    name: r.name,
    email: r.email,
    role: r.role,
    status: r.status,
    avatarHue: r.avatarHue,
    initials: r.initials,
    lastActive: r.lastActive,
  }
}

export function toBilling(r: RecordModel): Billing {
  return {
    planId: r.planId,
    used: r.used,
    allowance: r.allowance,
    renewsOn: r.renewsOn,
    seatsUsed: r.seatsUsed,
    card: r.card ?? { brand: '', last4: '', exp: '' },
    invoices: r.invoices ?? [],
    stripeCustomerId: r.stripeCustomerId || undefined,
    stripeSubscriptionId: r.stripeSubscriptionId || undefined,
    cancelAtPeriodEnd: !!r.cancelAtPeriodEnd,
  }
}

export function toAnnouncement(r: RecordModel): Announcement {
  return {
    id: r.id,
    title: r.title,
    body: r.body || '',
    severity: (r.severity as AnnouncementSeverity) || 'info',
    channels: Array.isArray(r.channels) ? r.channels : [],
    audience: r.audience || 'all',
    audiencePlan: r.audiencePlan || undefined,
    audienceAccountId: r.audienceAccountId || undefined,
    status: r.status || 'draft',
    publishedAt: r.publishedAt || undefined,
    expiresAt: r.expiresAt || undefined,
    dismissible: r.dismissible !== false,
  }
}

/** "Maya Chen" → "MC"; falls back to the first two characters. */
export function initialsOf(nameOrEmail: string): string {
  const base = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail
  const parts = base.split(/[\s._-]+/).filter(Boolean)
  const ini = parts.map((p) => p[0]).join('').slice(0, 2)
  return (ini || base.slice(0, 2)).toUpperCase()
}
